import { useId, type ReactElement } from 'react';
import { Modal } from './Modal';
import { CLIENT_COPY, type OffSheetCopy } from '../../../../../shared/client-copy.js';
import './PrivacyOffSheet.css';

export type PrivacyOffKey = 'share' | 'background';

/**
 * What each sheet says: both are one source for all three clients
 * (src/shared/client-copy.js, served to the apps over /api/app/config —
 * background 2026-10-21 Riley, share the same day).
 */
const SHEETS: Record<PrivacyOffKey, OffSheetCopy> = {
  share: CLIENT_COPY.legal.share.off_sheet,
  background: CLIENT_COPY.legal.background.off_sheet,
};

/**
 * The sheet that opens before an account-wide switch goes off (2026-09-12
 * 拍板): a plain, honest word about what the switch does and why Ember exists,
 * where to read the whole story, and two clear ways out — keep it on, or turn
 * it off. It is one moment of asking, not a wall: `Turn it off` is a real
 * button, right there. Only the two account-wide switches open it; the switch
 * on a single Ember flips in place.
 */
export function PrivacyOffSheet({ which, onKeep, onTurnOff }: {
  which: PrivacyOffKey;
  onKeep: () => void;
  onTurnOff: () => void;
}): ReactElement {
  const titleId = useId();
  const sheet = SHEETS[which];
  return (
    <Modal sheet="phone" onClose={onKeep} labelledBy={titleId} className="privacy-off">
      <div className="privacy-off-inner" data-log-seen="privacy_off_sheet" data-log-state={which}>
        <h2 id={titleId} className="privacy-off-title">{sheet.title}</h2>
        {sheet.body.map((p) => <p key={p}>{p}</p>)}
        <p className="privacy-off-links">
          <a href="/how-ember-works" data-log="privacy_off_read" data-log-state="how">How Ember works</a>
          <a href="/privacy-and-sharing" data-log="privacy_off_read" data-log-state="privacy">Privacy and sharing</a>
        </p>
        <div className="privacy-off-actions">
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="privacy_off_keep"
            data-log-state={which}
            onClick={onKeep}
            data-autofocus
          >
            {sheet.cancel}
          </button>
          <button
            type="button"
            className="ember-btn ember-btn-ghost"
            data-log={which === 'share' ? 'share_global_toggle' : 'background_global_toggle'}
            data-log-state="off"
            onClick={onTurnOff}
          >
            {sheet.confirm}
          </button>
        </div>
      </div>
    </Modal>
  );
}
